import {
  ThumbDown as ThumbDownIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOffAlt as ThumbUpOffAltIcon,
} from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import ChangeInterviewFeedbackModal from "components/ChangeInterviewFeedbackModal";
import { useAppContextController } from "context/AppContext";
import updateInterviewFeedback from "layouts/pages/events/actions/updateInterviewFeedback";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useMutation } from "react-query";

const InterviewFeedbackButton = ({
  applicant,
  user,
  refetchApplicants,
  onChangeFeedback,
  fontSize = "medium",
  isFinal,
  eventUrl,
  sx = { p: 0 },
  disableFinal,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [feedbackModalSelectedData, setFeedbackModalSelectedData] = useState(null);
  const { currentLoggedUser } = useAppContextController();

  const handleOpenFeedbackModal = (appl, usr, currentRating) => {
    setFeedbackModalSelectedData({ applicant: appl, user: usr, currentRating });
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModalSelectedData(null);
  };

  const thumbUpOptions = useMemo(
    () => [
      {
        label: "None",
        color: "action",
        icon: <ThumbUpIcon fontSize={fontSize} color="action" />,
      },
      {
        label: "Yes",
        color: "success",
        icon: <ThumbUpIcon fontSize={fontSize} color="success" />,
      },
      {
        label: "Maybe",
        color: "warning",
        icon: <ThumbUpIcon fontSize={fontSize} color="warning" />,
      },
      {
        label: "No",
        color: "error",
        icon: <ThumbDownIcon fontSize={fontSize} color="error" />,
      },
    ],
    [fontSize]
  );

  const thumbIcon = useMemo(() => {
    if (!applicant)
      return {
        label: "Select an applicant to show feedback",
        color: "action",
        icon: <ThumbUpOffAltIcon fontSize="medium" color="action" />,
      };
    if (isFinal) {
      const interviewItem =
        applicant?.interviews == null
          ? applicant?.interview
          : applicant?.interviews?.find((int) => int.eventUrl === eventUrl);
      if (!interviewItem) {
        return {
          ...thumbUpOptions[0],
          label: "Feedback",
        };
      }
      const result = thumbUpOptions.find((option) => {
        return option.label === interviewItem.feedback?.rating;
      });

      if (result)
        return {
          ...result,
          label: "Feedback",
        };
      return {
        ...thumbUpOptions[0],
        label: "Feedback",
      };
    }

    const feedbackItem = applicant?.feedback?.find(
      (fb) => fb.createAgent === (user?._id ?? user?.id)
    );
    if (!feedbackItem) {
      return {
        ...thumbUpOptions[0],
        label: "Feedback",
      };
    }
    const result = thumbUpOptions.find((option) => {
      return option.label === feedbackItem.rating;
    });
    if (result)
      return {
        ...result,
        label: "Feedback",
      };
    return {
      ...thumbUpOptions[0],
      label: "Feedback",
    };
  }, [applicant, isFinal, thumbUpOptions, eventUrl, user?._id, user?.id]);

  return (
    <>
      <Tooltip title={thumbIcon.label ?? "Feedback"} placement="top">
        <span>
          <IconButton
            sx={{ ...sx, ...(!applicant ? { opacity: 0.5 } : {}) }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFeedbackModal(applicant, user);
            }}
            disabled={!applicant}
          >
            {thumbIcon.icon}
          </IconButton>
        </span>
      </Tooltip>
      <button
        type="button"
        onClick={(e) => {
          e?.stopPropagation();
        }}
        style={{ height: 0, width: 0, padding: 0, margin: 0 }}
      >
        {!!feedbackModalSelectedData && (
          <ChangeInterviewFeedbackModal
            open={!!feedbackModalSelectedData}
            handleClose={handleCloseFeedbackModal}
            applicant={feedbackModalSelectedData.applicant}
            user={feedbackModalSelectedData.user}
            currentRating={feedbackModalSelectedData.currentRating}
            eventUrl={eventUrl}
            onChangeFeedback={({
              applicant: changeAppl,
              eventUrl: changeEventUrl,
              rating: changeRating,
              note: changeNote,
              isFinal: changeIsFinal,
            }) =>
              feedbackModalSelectedData.user
                ? onChangeFeedback?.({
                    applicant: feedbackModalSelectedData.applicant,
                    user: feedbackModalSelectedData.user,
                    eventUrl: changeEventUrl,
                    rating: changeRating,
                    note: changeNote,
                    isFinal: changeIsFinal,
                  })
                : onChangeFeedback?.({
                    applicant: feedbackModalSelectedData.applicant,
                    user: currentLoggedUser,
                    eventUrl: changeEventUrl,
                    rating: changeRating,
                    note: changeNote,
                    isFinal: changeIsFinal,
                  })
            }
            refetchApplicants={refetchApplicants}
            disableFinal={disableFinal}
          />
        )}
      </button>
    </>
  );
};

export default InterviewFeedbackButton;
